/* The backlog as a database, in the sense Notion means it.
 *
 * The list this replaces was a list: a title, a chip, a checkbox, sorted one
 * way for ever. That is fine for eleven things and useless for sixty, and it
 * could not answer any of the questions people actually open a backlog with —
 * what is late, what is for which project, what is small enough to do now,
 * what is not on a day yet.
 *
 * So the same rows, with the moves a database has:
 *
 *   · **Properties, in columns.** Every one is editable in place, and every
 *     one is a field the planner genuinely reads. The rule that kept this
 *     screen honest still holds — a column the solver ignores would quietly
 *     teach people the app is not listening — which is why there is no
 *     "priority score", no "effort points", and no status of your own
 *     invention. What is here is what decides your day.
 *
 *   · **Group by**, which is the move that turns a list into a view. By status
 *     it is a board; by type it is a project list; by when it is a schedule.
 *
 *   · **Sort and filter**, remembered per person rather than per session,
 *     because somebody who works by deadline works by deadline every day.
 *
 *   · **A page behind every row.** Notes, in prose, where the address and the
 *     three bullet points and the link go. Nothing reads it but you, and that
 *     is precisely why it is allowed to be free text.
 *
 * The one Notion thing deliberately not copied is a note that can contain a
 * task. The moment prose can hold a checkbox there are two task lists and they
 * disagree — and the whole product rests on there being one.
 *
 * ACCESSIBILITY. It is a real `<table>`: a caption, column headers with
 * `scope`, `aria-sort` on the header you sorted by, and a live region that
 * says how many rows a filter left. Everything editable is a real control with
 * a real label, so the row can be driven entirely from the keyboard, and the
 * detail panel is a disclosure with `aria-expanded`/`aria-controls` rather
 * than a div that appears.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ActivityType, Task, Urgency } from '@shared/types';
import { post, patch, del, errorMessage, readLocal, writeLocal } from '../../api';
import { useToast } from '../../ui/toast';
import { human, friendlyDate, nearLabel, hhmm } from '../../ui/time';
import { ScheduleDialog, ConfirmDialog } from '../../ui/schedule';
import Icon from '../../ui/Icon';
import type { IconName } from '../../ui/Icon';

export const URGENCY_LABEL: Record<Urgency, string> = {
  must: 'Must', should: 'Should', could: 'Could'
};

/* The solver's own ordering, mirrored so the table reads the way the day gets
   built. A backlog sorted differently from how it is consumed makes the
   planner look arbitrary. */
const URGENCY_RANK: Record<Urgency, number> = { must: 0, should: 1, could: 2 };

type SortKey = 'urgency' | 'due' | 'when' | 'size' | 'title';
type GroupKey = 'none' | 'status' | 'type' | 'urgency' | 'when';
type Filter = 'open' | 'unplanned' | 'late' | 'done' | 'all';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'urgency', label: 'Urgency' },
  { key: 'due', label: 'Deadline' },
  { key: 'when', label: 'When it happens' },
  { key: 'size', label: 'How big' },
  { key: 'title', label: 'Name' }
];

const GROUPS: { key: GroupKey; label: string }[] = [
  { key: 'none', label: 'Nothing' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Type' },
  { key: 'urgency', label: 'Urgency' },
  { key: 'when', label: 'When' }
];

const FILTERS: { key: Filter; label: string; hint: string; icon: IconName }[] = [
  { key: 'open', label: 'Open', hint: 'Everything still owed', icon: 'tasks' },
  { key: 'unplanned', label: 'Not on a day', hint: 'Owed, and nowhere near a plan yet', icon: 'clock' },
  { key: 'late', label: 'Late', hint: 'A deadline that has already gone past', icon: 'flag' },
  { key: 'done', label: 'Finished', hint: 'What you have already done', icon: 'check' },
  { key: 'all', label: 'Everything', hint: 'Open and finished together', icon: 'menu' }
];

/* Urgency reads at a glance as a coloured mark before the word, the way a
   priority does in every tracker people already use. */
const URGENCY_TONE: Record<Urgency, string> = { must: 'urg-must', should: 'urg-should', could: 'urg-could' };

/* Remembered on the device, not the account: a view is how you are working
   this afternoon, and syncing it would mean a laptop rearranging a phone. */
const VIEW_KEY = 'amon_task_view';

interface View { sort: SortKey; group: GroupKey; filter: Filter; desc: boolean }

const DEFAULT_VIEW: View = { sort: 'urgency', group: 'status', filter: 'open', desc: false };

function loadView(): View {
  try {
    const raw = readLocal(VIEW_KEY);
    return raw ? { ...DEFAULT_VIEW, ...JSON.parse(raw) } : DEFAULT_VIEW;
  } catch { return DEFAULT_VIEW; }
}

const statusOf = (t: Task) => t.done ? 'Finished' : t.placedAt ? 'On a day' : 'Not placed';

export default function TaskTable({ tasks, types, today, onChanged }: {
  tasks: Task[];
  types: ActivityType[];
  today: string;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [view, setView] = useState<View>(loadView);
  const [query, setQuery] = useState('');
  const [openRow, setOpenRow] = useState<string | null>(null);
  /* Which dialog is up, and about what. One value: two would let the screen
     ask two questions at once. */
  const [asking, setAsking] = useState<{ kind: 'when' | 'delete'; task: Task } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const statusId = useId();

  useEffect(() => { writeLocal(VIEW_KEY, JSON.stringify(view)); }, [view]);

  /* One mutation for every property, because they are all the same request.
     Optimistic: the previous list is kept so a refusal restores exactly what
     was there rather than refetching and folding in somebody else's change. */
  const edit = useMutation({
    mutationFn: async ({ task, patch: body }: { task: Task; patch: Partial<Record<string, unknown>> }) => {
      const r = await patch(`/api/tasks/${task.id}`, body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return { task, body };
    },
    onMutate: async ({ task, patch: body }) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const previous = qc.getQueryData<Task[]>(['tasks']);
      qc.setQueryData<Task[]>(['tasks'], old =>
        old?.map(t => t.id === task.id ? optimistic(t, body) : t));
      return { previous };
    },
    onError: (e: Error, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['tasks'], ctx.previous);
      toast.say(e.message, 'bad');
    },
    onSuccess: ({ task, body }) => {
      onChanged();
      if (body.done === true) {
        toast.show({
          message: `Done: ${task.title}`,
          tone: 'good',
          action: { label: 'Undo', run: () => edit.mutate({ task, patch: { done: false } }) }
        });
      } else if (body.done === false) toast.say(`Back on the list: ${task.title}`);
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['tasks'] }); }
  });

  /* Delete is the one action here that cannot be undone by the same path — the
     row is gone. So it asks first, rather than offering a reversal it cannot
     honour. */
  const remove = useMutation({
    mutationFn: async (task: Task) => {
      const r = await del(`/api/tasks/${task.id}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not delete that.'));
      return task;
    },
    onSuccess: t => { onChanged(); setOpenRow(null); toast.say(`Deleted “${t.title}”.`); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  /* ── putting one on a day yourself ────────────────────────────
     The planner could always place a task; you could not, and “no, not then”
     had no way to be said. Placing and moving are the same request to two
     different endpoints, so they are the same dialog. */
  async function place(task: Task, when: { date: string; startMin: number; minutes: number }) {
    const r = task.placedAt
      ? await patch(`/api/plan/block/${task.placedAt.blockId}`, when)
      : await post('/api/plan/block', { ...when, title: task.title, taskId: task.id });
    /* The server's own sentence — “Overlaps “Design review” — a fixed meeting
       with other people” — is what the dialog shows, because it names the thing
       to move rather than reporting that a write failed. */
    if (!r.ok) throw new Error(errorMessage(r, 'Could not put it there.'));
    onChanged();
    toast.say(`“${task.title}” is on ${friendlyDate(when.date)} at ${hhmm(when.startMin)}.`, 'good');
  }

  const unschedule = useMutation({
    mutationFn: async (task: Task) => {
      const r = await post(`/api/tasks/${task.id}/unschedule`, {});
      if (!r.ok) throw new Error(errorMessage(r, 'Could not take it off.'));
      return task;
    },
    onSuccess: t => {
      onChanged();
      toast.say(`“${t.title}” is back in the backlog. Amon can place it again, or you can.`);
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = tasks.filter(t => {
      if (q && !(t.title.toLowerCase().includes(q) || (t.notes || '').toLowerCase().includes(q)))
        return false;
      switch (view.filter) {
        case 'open': return !t.done;
        case 'unplanned': return !t.done && !t.placedAt;
        case 'late': return !t.done && !!t.due && t.due < today;
        case 'done': return t.done;
        default: return true;
      }
    });

    const dir = view.desc ? -1 : 1;
    const by: Record<SortKey, (a: Task, b: Task) => number> = {
      urgency: (a, b) => (URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency]) || (b.mins - a.mins),
      /* Something with no deadline is not "before 1970" and not "after
         everything" — it is simply not in this ordering, so it goes last
         whichever way the arrow points. */
      due: (a, b) => (a.due ? 0 : 1) - (b.due ? 0 : 1) || String(a.due).localeCompare(String(b.due)),
      when: (a, b) => (a.placedAt ? 0 : 1) - (b.placedAt ? 0 : 1) ||
        String(a.placedAt?.date).localeCompare(String(b.placedAt?.date)) ||
        (a.placedAt?.startMin ?? 0) - (b.placedAt?.startMin ?? 0),
      size: (a, b) => b.mins - a.mins,
      title: (a, b) => a.title.localeCompare(b.title)
    };
    list = [...list].sort((a, b) => by[view.sort](a, b) * dir);
    return list;
  }, [tasks, query, view, today]);

  const groups = useMemo(() => group(rows, view.group, types, today), [rows, view.group, types, today]);

  const setView2 = (patch: Partial<View>) => setView(v => ({ ...v, ...patch }));

  const toggleSort = (key: SortKey) =>
    setView2(key === view.sort ? { desc: !view.desc } : { sort: key, desc: false });

  const ariaSort = (key: SortKey): 'ascending' | 'descending' | 'none' =>
    view.sort !== key ? 'none' : view.desc ? 'descending' : 'ascending';

  return (
    <section className="db" aria-labelledby="db-title">
      <div className="panel-head">
        <h2 id="db-title" className="db-title"><Icon name="tasks" size={17} />Your list</h2>
        <span className="muted small">{FILTERS.find(f => f.key === view.filter)?.hint}</span>
      </div>

      <div className="db-bar">
        <div className="db-filters" role="group" aria-label="Which tasks to show">
          {FILTERS.map(f => (
            <button key={f.key}
                    className={`pill${view.filter === f.key ? ' is-active' : ''}`}
                    aria-pressed={view.filter === f.key}
                    title={f.hint}
                    onClick={() => setView2({ filter: f.key })}><Icon name={f.icon} size={14} />{f.label}</button>
          ))}
        </div>

        <div className="db-controls">
          <label className="db-field db-search">
            <span>Search</span>
            <span className="input-with-icon">
              <Icon name="search" size={15} />
              <input type="search" value={query} placeholder="Name or notes"
                     onChange={e => setQuery(e.target.value)} />
            </span>
          </label>
          <label className="db-field">
            <span>Group by</span>
            <select value={view.group}
                    onChange={e => setView2({ group: e.target.value as GroupKey })}>
              {GROUPS.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
          </label>
          <label className="db-field">
            <span>Sort by</span>
            <select value={view.sort}
                    onChange={e => setView2({ sort: e.target.value as SortKey })}>
              {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <button className="btn btn-ghost btn-icon"
                  aria-label={view.desc ? 'Sort ascending' : 'Sort descending'}
                  title={view.desc ? 'Sort ascending' : 'Sort descending'}
                  onClick={() => setView2({ desc: !view.desc })}>
            <Icon name={view.desc ? 'sortDesc' : 'sortAsc'} size={17} />
          </button>
        </div>
      </div>

      {/* Said out loud, because a filter that changes what is on screen is
          invisible to somebody who cannot see the screen change. */}
      <p className="muted small" id={statusId} role="status" aria-live="polite">
        {rows.length} {rows.length === 1 ? 'task' : 'tasks'} shown
        {query && ` matching “${query}”`}
      </p>

      {rows.length === 0 && (
        <div className="empty">
          <h3>{query ? 'Nothing matches that' : 'Nothing here'}</h3>
          <p className="muted">
            {query
              ? 'Try a shorter search, or a different filter.'
              : view.filter === 'open'
                ? 'Nothing owed. When you take something on, put it here and Amon finds the hours.'
                : 'No tasks in this view.'}
          </p>
        </div>
      )}

      {rows.length > 0 && groups.map(g => (
        <div className="db-group" key={g.key}>
          {view.group !== 'none' && (
            <button className="db-group-head"
                    aria-expanded={!collapsed.has(g.key)}
                    onClick={() => setCollapsed(prev => {
                      const next = new Set(prev);
                      next.has(g.key) ? next.delete(g.key) : next.add(g.key);
                      return next;
                    })}>
              <span className="db-caret" aria-hidden="true"><Icon name={collapsed.has(g.key) ? 'chevronRight' : 'chevronDown'} size={15} /></span>
              <strong>{g.label}</strong>
              <span className="chip">{g.rows.length}</span>
              {g.mins > 0 && <span className="muted small">{human(g.mins)}</span>}
            </button>
          )}

          {!collapsed.has(g.key) && (
            <div className="db-scroll">
              <table className="db-table">
                <caption className="sr-only">
                  {g.label === 'All' ? 'Your tasks' : `Tasks: ${g.label}`}, sorted by{' '}
                  {SORTS.find(s => s.key === view.sort)?.label.toLowerCase()}
                </caption>
                {/* Proportional widths rather than a sideways scroll: a
                    column somebody has to drag to find is a column they do not
                    know exists. */}
                <colgroup>
                  <col className="c-check" />
                  <col className="c-name" />
                  <col className="c-when" />
                  <col className="c-type" />
                  <col className="c-urg" />
                  <col className="c-size" />
                  <col className="c-due" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" className="db-check">
                      <span className="sr-only">Finished</span>
                    </th>
                    <th scope="col" aria-sort={ariaSort('title')}>
                      <button className="db-sort" onClick={() => toggleSort('title')}>Name</button>
                    </th>
                    <th scope="col" aria-sort={ariaSort('when')}>
                      <button className="db-sort" onClick={() => toggleSort('when')}>When</button>
                    </th>
                    <th scope="col">Type</th>
                    <th scope="col" aria-sort={ariaSort('urgency')}>
                      <button className="db-sort" onClick={() => toggleSort('urgency')}>Urgency</button>
                    </th>
                    <th scope="col" aria-sort={ariaSort('size')}>
                      <button className="db-sort" onClick={() => toggleSort('size')}>How big</button>
                    </th>
                    <th scope="col" aria-sort={ariaSort('due')}>
                      <button className="db-sort" onClick={() => toggleSort('due')}>Deadline</button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map(t => (
                    <Row key={t.id} task={t} types={types} today={today}
                         open={openRow === t.id}
                         onToggle={() => setOpenRow(openRow === t.id ? null : t.id)}
                         onEdit={body => edit.mutate({ task: t, patch: body })}
                         onSchedule={() => setAsking({ kind: 'when', task: t })}
                         onUnschedule={() => unschedule.mutate(t)}
                         onDelete={() => setAsking({ kind: 'delete', task: t })} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {asking?.kind === 'when' && (
        <ScheduleDialog
          title={asking.task.placedAt ? 'Move it' : 'Put it on a day'}
          what={asking.task.title}
          description={asking.task.placedAt
            ? 'It keeps its place in the backlog either way — this only changes when it happens.'
            : 'Pinned once you place it, so the planner works around it instead of moving it.'}
          initial={{
            date: asking.task.placedAt?.date ?? today,
            startMin: asking.task.placedAt?.startMin ?? 9 * 60,
            minutes: asking.task.placedAt?.minutes ?? asking.task.mins
          }}
          confirmLabel={asking.task.placedAt ? 'Move it' : 'Put it on the day'}
          onClose={() => setAsking(null)}
          onSubmit={when => place(asking.task, when)} />
      )}

      {asking?.kind === 'delete' && (
        <ConfirmDialog
          title={`Delete “${asking.task.title}”?`}
          description={asking.task.placedAt
            ? 'This cannot be undone, and it comes off your day as well.'
            : 'This cannot be undone.'}
          confirmLabel="Delete it"
          danger
          onClose={() => setAsking(null)}
          onConfirm={() => remove.mutateAsync(asking.task)} />
      )}
    </section>
  );
}

/* ── one row, and the page behind it ─────────────────────────── */

function Row({ task, types, today, open, onToggle, onEdit, onSchedule, onUnschedule, onDelete }: {
  task: Task;
  types: ActivityType[];
  today: string;
  open: boolean;
  onToggle: () => void;
  onEdit: (patch: Record<string, unknown>) => void;
  onSchedule: () => void;
  onUnschedule: () => void;
  onDelete: () => void;
}) {
  const detailId = `task-detail-${task.id}`;
  const late = !task.done && !!task.due && task.due < today;

  return (
    <>
      <tr className={`db-row${task.done ? ' is-done' : ''}${open ? ' is-open' : ''}`}>
        <td className="db-check">
          <input type="checkbox" checked={task.done}
                 aria-label={task.done ? `Put back on the list: ${task.title}` : `Mark done: ${task.title}`}
                 onChange={e => onEdit({ done: e.target.checked })} />
        </td>

        <th scope="row" className="db-name">
          {/* The row header is the disclosure. One control, and it is the
              thing you would click anyway. */}
          <button className="db-open" aria-expanded={open} aria-controls={detailId}
                  onClick={onToggle}>
            <span className="db-caret" aria-hidden="true"><Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} /></span>
            <span>{task.title}</span>
          </button>
          {task.notes && <span className="chip chip-soft" title={task.notes}><Icon name="edit" size={11} />note</span>}
          {task.imported && task.externalUrl && (
            <a className="muted small" href={task.externalUrl} target="_blank" rel="noreferrer noopener">
              {task.src}
            </a>
          )}
          {task.push?.state === 'failed' && (
            <span className="push-note push-failed">Not saved to {task.src}</span>
          )}
        </th>

        {/* WHEN it is, not merely that it is somewhere. "On a day" was the
            whole answer this screen used to give. */}
        <td className="db-when" data-label="When">
          {task.placedAt ? (
            <span title={friendlyDate(task.placedAt.date)}>
              {nearLabel(task.placedAt.date, today) ?? friendlyDate(task.placedAt.date, true)}
              {' '}<em>{task.placedAt.start}</em>
            </span>
          ) : (
            <span className="muted">Not placed</span>
          )}
        </td>

        <td data-label="Type">
          <select className="db-cell" value={task.typeId ?? ''}
                  aria-label={`Type for ${task.title}`}
                  onChange={e => onEdit({ typeId: e.target.value || null })}>
            <option value="">—</option>
            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </td>

        <td className={`db-urg ${URGENCY_TONE[task.urgency]}`} data-label="Urgency">
          <select className="db-cell" value={task.urgency}
                  aria-label={`Urgency for ${task.title}`}
                  onChange={e => onEdit({ urgency: e.target.value as Urgency })}>
            {(Object.keys(URGENCY_LABEL) as Urgency[]).map(u =>
              <option key={u} value={u}>{URGENCY_LABEL[u]}</option>)}
          </select>
        </td>

        <td className="db-size" data-label="Size">
          <input className="db-cell" type="number" min="5" max="600" step="5"
                 aria-label={`Minutes for ${task.title}`}
                 defaultValue={task.mins}
                 onBlur={e => {
                   const mins = Math.max(5, Math.min(600, Number(e.target.value) || task.mins));
                   if (mins !== task.mins) onEdit({ estMin: mins });
                 }} />
          {task.doneMin > 0 && (
            <span className="muted small" title="Measured against your estimate">
              {human(task.doneMin)} spent
            </span>
          )}
        </td>

        <td className={late ? 'db-late' : undefined} data-label="Deadline">
          <input className="db-cell" type="date" value={task.due ?? ''}
                 aria-label={`Deadline for ${task.title}`}
                 onChange={e => onEdit({ due: e.target.value || null })} />
          {late && <span className="sr-only">Late</span>}
        </td>
      </tr>

      {open && (
        <tr className="db-detail-row">
          <td colSpan={7}>
            <div className="db-detail" id={detailId}>
              <Notes task={task} onSave={notes => onEdit({ notes })} />

              <div className="db-detail-side">
                <dl className="db-facts">
                  <dt>Status</dt><dd>{statusOf(task)}</dd>
                  <dt>Source</dt><dd>{task.src}</dd>
                  {task.placedAt && (
                    <>
                      <dt>Planned</dt>
                      <dd>{friendlyDate(task.placedAt.date)} at {task.placedAt.start}</dd>
                    </>
                  )}
                  {task.alarms.length > 0 && (
                    <>
                      <dt>Reminders</dt>
                      <dd>{task.alarms.map(m => human(m)).join(', ')} before</dd>
                    </>
                  )}
                  {task.doneMin > 0 && (
                    <>
                      <dt>Measured</dt>
                      <dd>{human(task.doneMin)} against {task.est} estimated</dd>
                    </>
                  )}
                </dl>
                <div className="db-actions">
                  <button className="btn btn-ghost btn-sm" onClick={onSchedule}>
                    <Icon name={task.placedAt ? 'move' : 'clock'} size={15} />
                    {task.placedAt ? 'Move it' : 'Put it on a day'}
                  </button>
                  {task.placedAt && (
                    <button className="btn btn-ghost btn-sm" onClick={onUnschedule}>
                      <Icon name="undo" size={15} />Take it off the day
                    </button>
                  )}
                  <button className="btn btn-ghost btn-sm btn-bad db-delete" onClick={onDelete}>
                    <Icon name="trash" size={15} />Delete this task
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* Saved on blur rather than on every keystroke: a note is prose, and a request
   per character is a request per character. */
function Notes({ task, onSave }: { task: Task; onSave: (notes: string) => void }) {
  const [text, setText] = useState(task.notes ?? '');
  useEffect(() => { setText(task.notes ?? ''); }, [task.id, task.notes]);
  const id = `notes-${task.id}`;

  return (
    <div className="db-notes">
      <label htmlFor={id}>Notes</label>
      <textarea id={id} value={text} rows={5} maxLength={4000}
                placeholder="Anything you need in order to start: the address, the three bullet points, the link."
                onChange={e => setText(e.target.value)}
                onBlur={() => { if (text !== (task.notes ?? '')) onSave(text); }} />
      <p className="muted small">
        Yours to read. Nothing here changes the plan — it is the page behind the task.
      </p>
    </div>
  );
}

/* ── grouping ───────────────────────────────────────────────── */

/* The request speaks the API's words and the list speaks the task's. `estMin`
   is `mins` on a task, and writing it through unchanged left the cell showing
   the old size until the refetch landed. */
function optimistic(t: Task, body: Record<string, unknown>): Task {
  const { estMin, ...rest } = body;
  const next = { ...t, ...(rest as Partial<Task>) };
  if (typeof estMin === 'number') {
    next.mins = estMin;
    next.est = estMin >= 60 ? `${(estMin / 60).toFixed(estMin % 60 ? 1 : 0)}h` : `${estMin}m`;
  }
  return next;
}

interface Group { key: string; label: string; rows: Task[]; mins: number }

function group(rows: Task[], by: GroupKey, types: ActivityType[], today: string): Group[] {
  if (by === 'none')
    return [{ key: 'all', label: 'All', rows, mins: rows.reduce((a, t) => a + t.mins, 0) }];

  const nameOf = (t: Task) => {
    switch (by) {
      case 'status': return statusOf(t);
      case 'type': return types.find(x => x.id === t.typeId)?.name ?? 'No type';
      case 'urgency': return URGENCY_LABEL[t.urgency];
      case 'when': return t.placedAt
        ? (nearLabel(t.placedAt.date, today) ?? friendlyDate(t.placedAt.date, true))
        : 'Not placed';
      default: return 'All';
    }
  };

  /* Insertion order, which is the sorted order — so a group's position follows
     the sort rather than the alphabet. "Must" above "Could" is the point. */
  const map = new Map<string, Task[]>();
  for (const t of rows) {
    const k = nameOf(t);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(t);
  }
  return [...map.entries()].map(([label, list]) => ({
    key: label, label, rows: list, mins: list.reduce((a, t) => a + t.mins, 0)
  }));
}

export { hhmm };
